import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import { Mail, Mic, Calendar as CalendarIcon, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const speakingTopics = [
  "Grief Support",
  "Emotional Resilience",
  "Mental Wellness",
  "Caregiver Encouragement",
  "Workplace Stress & Burnout",
  "Corporate Chaplaincy",
  "Leadership Encouragement",
  "Community Support",
];

const EncourageAndSpeaking = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loadingNl, setLoadingNl] = useState(false);

  const [open, setOpen] = useState(false);
  const [loadingSp, setLoadingSp] = useState(false);
  const [sp, setSp] = useState({
    name: "",
    organization: "",
    event_type: "",
    event_date: "",
    location: "",
    phone: "",
    email: "",
    audience_type: "",
    estimated_audience_size: "",
    topics: [],
    message: "",
  });
  const updateSp = (k, v) => setSp((s) => ({ ...s, [k]: v }));
  const toggleTopic = (t) =>
    setSp((s) => ({
      ...s,
      topics: s.topics.includes(t)
        ? s.topics.filter((x) => x !== t)
        : [...s.topics, t],
    }));

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoadingNl(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        first_name: firstName || null,
      });
      toast.success("You're in. Look for gentle encouragement soon.");
      setEmail("");
      setFirstName("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not subscribe right now. Please try again."
      );
    } finally {
      setLoadingNl(false);
    }
  };

  const submitSpeaking = async (e) => {
    e.preventDefault();
    if (!sp.name || !sp.event_type || !sp.email) {
      toast.error("Please share your name, event type, and email.");
      return;
    }
    setLoadingSp(true);
    try {
      await axios.post(`${API}/speaking-requests`, {
        ...sp,
        topics: sp.topics.length ? sp.topics : null,
      });
      toast.success(
        "Thank you. Your speaking request was received — we'll be in touch."
      );
      setOpen(false);
      setSp({
        name: "",
        organization: "",
        event_type: "",
        event_date: "",
        location: "",
        phone: "",
        email: "",
        audience_type: "",
        estimated_audience_size: "",
        topics: [],
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not submit. Please try again."
      );
    } finally {
      setLoadingSp(false);
    }
  };

  return (
    <section
      id="speaking"
      data-testid="encourage-speaking-section"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Encouragement Signup */}
          <div className="lg:col-span-6 rounded-2xl bg-[#F0F9FF] p-8 lg:p-10 border border-sky-100">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#0369A1]">
              Free Encouragement
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
              A quiet word in your inbox.
            </h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">
              Short, honest reflections you can read in five minutes — gentle
              encouragement for ordinary, difficult weeks. No noise.
              Unsubscribe anytime.
            </p>
            <form
              onSubmit={submitNewsletter}
              data-testid="newsletter-form"
              className="mt-6 space-y-3"
            >
              <input
                type="text"
                placeholder="First name (optional)"
                data-testid="newsletter-name-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-full border border-slate-200 bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-5 py-3 text-sm"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  required
                  type="email"
                  placeholder="you@example.com"
                  data-testid="newsletter-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 rounded-full border border-slate-200 bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-5 py-3 text-sm"
                />
                <button
                  type="submit"
                  disabled={loadingNl}
                  data-testid="newsletter-submit-button"
                  className="bg-[#0369A1] hover:bg-[#0284C7] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap"
                >
                  {loadingNl ? "Sending..." : "Send me encouragement"}
                </button>
              </div>
            </form>
          </div>

          {/* Speaking */}
          <div className="lg:col-span-6 rounded-2xl bg-white p-8 lg:p-10 border border-slate-100 lift">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#D97706]">
              Speaking &amp; Invitations
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
              Invite Chaplain Derrick to speak.
            </h2>
            <p className="mt-4 text-slate-600 text-base leading-relaxed">
              Chaplain Derrick is available for selected workplaces, community
              events, care teams, support groups, caregiving events, leadership
              gatherings, churches, and educational presentations — including
              corporate chaplaincy outreach.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {speakingTopics.slice(0, 6).map((t) => (
                <span
                  key={t}
                  className="text-[11px] uppercase tracking-widest text-slate-600 bg-slate-50 border border-slate-100 rounded-full px-3 py-1"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button
                    data-testid="open-speaking-dialog"
                    className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
                  >
                    <Mic size={14} /> Send a speaking request
                  </button>
                </DialogTrigger>
                <DialogContent
                  className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                  data-testid="speaking-dialog"
                >
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                      Speaking Request
                    </DialogTitle>
                    <DialogDescription>
                      Share a few details about your event and Chaplain Derrick
                      will follow up personally.
                    </DialogDescription>
                  </DialogHeader>

                  <form
                    onSubmit={submitSpeaking}
                    data-testid="speaking-form"
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        required
                        placeholder="Your name *"
                        data-testid="sp-name"
                        value={sp.name}
                        onChange={(e) => updateSp("name", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        placeholder="Organization"
                        data-testid="sp-org"
                        value={sp.organization}
                        onChange={(e) => updateSp("organization", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        required
                        placeholder="Event type * (e.g., workshop, church service)"
                        data-testid="sp-event-type"
                        value={sp.event_type}
                        onChange={(e) => updateSp("event_type", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm sm:col-span-2"
                      />
                      <input
                        type="date"
                        placeholder="Event date"
                        data-testid="sp-event-date"
                        value={sp.event_date}
                        onChange={(e) => updateSp("event_date", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        placeholder="Location"
                        data-testid="sp-location"
                        value={sp.location}
                        onChange={(e) => updateSp("location", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        type="email"
                        required
                        placeholder="Email *"
                        data-testid="sp-email"
                        value={sp.email}
                        onChange={(e) => updateSp("email", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        placeholder="Phone"
                        data-testid="sp-phone"
                        value={sp.phone}
                        onChange={(e) => updateSp("phone", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        placeholder="Audience type (church, workplace, support group...)"
                        data-testid="sp-audience-type"
                        value={sp.audience_type}
                        onChange={(e) => updateSp("audience_type", e.target.value)}
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                      <input
                        placeholder="Estimated audience size"
                        data-testid="sp-audience-size"
                        value={sp.estimated_audience_size}
                        onChange={(e) =>
                          updateSp("estimated_audience_size", e.target.value)
                        }
                        className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-widest mb-2">
                        Topics of interest
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {speakingTopics.map((t) => {
                          const active = sp.topics.includes(t);
                          return (
                            <button
                              type="button"
                              key={t}
                              data-testid={`sp-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                              onClick={() => toggleTopic(t)}
                              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                                active
                                  ? "bg-[#0369A1] text-white border-[#0369A1]"
                                  : "bg-white text-slate-700 border-slate-200 hover:border-[#0369A1]"
                              }`}
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <textarea
                      rows={4}
                      placeholder="Message / Speaking needs"
                      data-testid="sp-message"
                      value={sp.message}
                      onChange={(e) => updateSp("message", e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm resize-none"
                    />

                    <DialogFooter>
                      <button
                        type="submit"
                        disabled={loadingSp}
                        data-testid="sp-submit"
                        className="inline-flex items-center gap-2 bg-[#0369A1] hover:bg-[#0284C7] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
                      >
                        {loadingSp ? "Sending..." : "Submit Request"}
                      </button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <a
                href="mailto:office@balministries.org?subject=Speaking%20Inquiry%20—%20Chaplain%20Derrick"
                data-testid="speaking-email-link"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#0369A1] text-slate-800 rounded-full px-6 py-3 text-sm font-medium transition-colors"
              >
                <Mail size={14} /> Email directly
              </a>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <CalendarIcon size={14} className="text-[#0369A1]" /> Booking 2025–26
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#0369A1]" /> Local &amp; virtual
              </div>
              <div className="flex items-center gap-2">
                <Mic size={14} className="text-[#0369A1]" /> 30–90 min sessions
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EncourageAndSpeaking;
